import type { AccessSettingsPublic, AccessSwitchesPublic, RoleAccessSwitches } from '@accounts/shared';

/**
 * Settings → Access: the six fixed switches (board, SIR-7864). Rows are what a role may do,
 * columns are the two limited roles; administrators always have all of them, so they have
 * no column. Each switch is stored on its own. Export Matrix has no effect for a role
 * without See Matrix: the server resolves that, and the editor says so.
 *
 * Pure helpers, kept apart from the editor component so they can be tested on their own.
 */

export type SwitchRole = keyof AccessSwitchesPublic;
export type SwitchKey = keyof RoleAccessSwitches;

export const SWITCH_ROLES: readonly SwitchRole[] = ['hr', 'it'];
export const SWITCH_KEYS: readonly SwitchKey[] = ['seeMatrix', 'exportMatrix', 'seeCatalog'];

export function withSwitch(
  switches: AccessSwitchesPublic,
  role: SwitchRole,
  key: SwitchKey,
  value: boolean,
): AccessSwitchesPublic {
  return { ...switches, [role]: { ...switches[role], [key]: value } };
}

export function switchesEqual(a: AccessSwitchesPublic, b: AccessSwitchesPublic): boolean {
  return SWITCH_ROLES.every((role) => SWITCH_KEYS.every((key) => a[role][key] === b[role][key]));
}

/** The roles whose Export Matrix is ticked while their See Matrix is not. */
export function exportWithoutSee(switches: AccessSwitchesPublic): SwitchRole[] {
  return SWITCH_ROLES.filter((role) => switches[role].exportMatrix && !switches[role].seeMatrix);
}

/** What the editor is keyed on, so a landed save re-seeds its draft by remounting. */
export function accessSavedSignature(saved: AccessSettingsPublic): string {
  const bits = SWITCH_ROLES.flatMap((role) =>
    SWITCH_KEYS.map((key) => (saved.switches[role][key] ? '1' : '0')),
  );
  return `${String(saved.deprovisionOverdueHours)}:${bits.join('')}`;
}
